import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { X, Loader2, CheckCircle2, Pencil, Download, AlertCircle } from "lucide-react";
import { AgreementProgressSection } from "./AgreementProgressSection";
import { groupThousands } from "@/shared/lib/money";
import type { AgreementPaymentSlot } from "../types";

// Snapshot of everything the operator entered, assembled by AgreementAddDrawer at the moment
// "Ko'rib chiqish" is pressed. The modal is read-only — editing happens back on the drawer.
export interface ContractPreviewData {
  merchantName: string;
  client: { name: string; phone: string; passport?: string; pin?: string };
  agreement: { typeLabel: string; productName: string; color?: string; note?: string };
  money: {
    cost_price: string;
    selling_price: string;
    down_payment: string;
    monthly_payment: string;
    payment_count: string;
    paid_amount: string;
  };
  // Projected payment schedule (same component the in-drawer summary + info-modal use). null → hidden.
  schedule: {
    payments: AgreementPaymentSlot[];
    paidAmount: string;
    remainder: string;
    startDate?: string | null;
  } | null;
  notification: { first_payment_date: string; payment_day: string; notification_before: string };
}

// A value-over-label pair, mirroring the mobile preview's SubtitleWidget (value on top, field name
// beneath). `emphasis` renders the monthly-payment value in brand green like the app's bold row.
function Pair({
  label,
  value,
  emphasis = false,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div>
      <p
        className={
          emphasis
            ? "text-[14px] font-semibold text-green-dark tnum"
            : "text-[14px] font-medium text-grey-800 tnum"
        }
      >
        {value}
      </p>
      <p className="mt-0.5 text-[12px] text-grey-500">{label}</p>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-grey-400">
      {children}
    </p>
  );
}

// "Shartnoma" contract preview shown AFTER the agreement is created but BEFORE the operator confirms
// it (0122). The "Ko'rib chiqish" button POSTs the agreement, then this modal shows the real WeasyPrint
// contract PDF in an <iframe> — mirroring the merchant app's preview. The operator can download the PDF,
// confirm (keep the saved agreement), or cancel/edit (Tahrirlash/X/backdrop → rollback = discard the
// just-created Loan). When the PDF can't be fetched (flag off / 404 / network) the drawer passes
// pdfUrl=null and this falls back to the HTML summary built from `data` — like the app's `pdfFailed`.
export function ContractPreviewModal({
  data,
  pdfUrl,
  isPending,
  onConfirm,
  onCancel,
  onDownload,
}: {
  data: ContractPreviewData;
  // Object URL of the fetched contract PDF, or null → render the HTML summary fallback.
  pdfUrl: string | null;
  isPending: boolean;
  onConfirm: () => void;
  // Tahrirlash / X / backdrop — rolls back the unconfirmed add (discard) in the parent.
  onCancel: () => void;
  // Download the PDF (only shown when pdfUrl is present). Parent owns the blob + filename.
  onDownload: () => void;
}) {
  const { t } = useTranslation();

  // Money fields are raw digit strings (USD-scale, like the app). Blank → em dash. Mirror the app's "$".
  const money = (v: string) => (v.trim() ? `$ ${groupThousands(v)}` : "—");
  const plain = (v: string) => (v.trim() ? v.trim() : "—");

  const { client, agreement, money: m, schedule, notification: n } = data;
  const hasPdf = pdfUrl !== null;

  // Portal to document.body so the backdrop covers the viewport and sits above the open drawer.
  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 sm:items-center"
      onClick={(e) => e.target === e.currentTarget && !isPending && onCancel()}
    >
      <div
        className={`relative z-[61] flex max-h-[92vh] w-full flex-col rounded-t-card bg-white shadow-float sm:rounded-card ${
          hasPdf ? "max-w-3xl" : "max-w-2xl"
        }`}
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-grey-100 px-4 py-4 sm:px-6">
          <div className="min-w-0">
            <h3 className="text-[16px] font-semibold text-grey-800">
              {t("import.agreementAdd.preview.title")}
            </h3>
            <p className="mt-0.5 truncate text-[12px] text-grey-500">
              {t("import.agreementAdd.preview.subtitle")} · {data.merchantName}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            className="text-grey-400 hover:text-grey-700 disabled:opacity-50"
            aria-label={t("import.agreementAdd.preview.editBtn")}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body — real PDF when available, else the HTML summary fallback. */}
        {hasPdf ? (
          <div className="flex-1 overflow-hidden px-4 py-5 sm:px-6">
            <iframe
              src={pdfUrl}
              title={t("import.agreementAdd.preview.title")}
              className="h-[70vh] w-full rounded-card border border-grey-100"
            />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-6">
            {/* PDF couldn't be fetched — show the entered data instead. */}
            <div className="mb-5 flex items-center gap-2 rounded-card bg-amber-50 px-4 py-2.5 text-[13px] text-amber-700">
              <AlertCircle size={16} strokeWidth={2} className="shrink-0" />
              {t("import.agreementAdd.preview.pdfError")}
            </div>

            {/* Mijoz */}
            <section className="mb-6">
              <SectionTitle>{t("import.agreementAdd.preview.clientSection")}</SectionTitle>
              <div className="divide-y divide-grey-100 rounded-card border border-grey-100">
                <div className="px-4 py-3">
                  <Pair label={t("import.agreementAdd.preview.clientName")} value={client.name || "—"} />
                </div>
                <div className="px-4 py-3">
                  <Pair label={t("import.agreementAdd.preview.clientPhone")} value={client.phone || "—"} />
                </div>
                {(client.passport || client.pin) && (
                  <div className="grid grid-cols-2 gap-4 px-4 py-3">
                    {client.passport && (
                      <Pair label={t("import.agreementAdd.preview.clientPassport")} value={client.passport} />
                    )}
                    {client.pin && (
                      <Pair label={t("import.agreementAdd.preview.clientPin")} value={client.pin} />
                    )}
                  </div>
                )}
              </div>
            </section>

            {/* Shartnoma */}
            <section className="mb-6">
              <SectionTitle>{t("import.agreementAdd.preview.agreementSection")}</SectionTitle>
              <div className="divide-y divide-grey-100 rounded-card border border-grey-100">
                <div className="grid grid-cols-2 gap-4 px-4 py-3">
                  <Pair label={t("import.agreementAdd.fields.type")} value={agreement.typeLabel} />
                  <Pair label={t("import.agreementAdd.fields.name")} value={agreement.productName || "—"} />
                </div>
                {(agreement.color || agreement.note) && (
                  <div className="grid grid-cols-2 gap-4 px-4 py-3">
                    {agreement.color && (
                      <Pair label={t("import.agreementAdd.fields.color")} value={agreement.color} />
                    )}
                    {agreement.note && (
                      <Pair label={t("import.agreementAdd.fields.note")} value={agreement.note} />
                    )}
                  </div>
                )}
              </div>
            </section>

            {/* Moliyaviy */}
            <section className="mb-6">
              <SectionTitle>{t("import.agreementAdd.preview.financialSection")}</SectionTitle>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4 rounded-card border border-grey-100 px-4 py-4 sm:grid-cols-3">
                <Pair label={t("import.agreementAdd.fields.cost_price")} value={money(m.cost_price)} />
                <Pair label={t("import.agreementAdd.fields.selling_price")} value={money(m.selling_price)} />
                <Pair label={t("import.agreementAdd.fields.down_payment")} value={money(m.down_payment)} />
                <Pair label={t("import.agreementAdd.fields.payment_count")} value={plain(m.payment_count)} />
                <Pair
                  label={t("import.agreementAdd.fields.monthly_payment")}
                  value={money(m.monthly_payment)}
                  emphasis
                />
                <Pair label={t("import.agreementAdd.fields.paid_amount")} value={money(m.paid_amount)} />
              </div>
            </section>

            {/* Jadval — projected schedule (segment bar + summary tiles) */}
            {schedule && (
              <section className="mb-6">
                <SectionTitle>{t("import.agreementAdd.preview.scheduleSection")}</SectionTitle>
                <div className="rounded-card border border-grey-100 px-4 pt-4">
                  <AgreementProgressSection
                    payments={schedule.payments}
                    paidAmount={schedule.paidAmount}
                    remainder={schedule.remainder}
                    startDate={schedule.startDate}
                  />
                </div>
              </section>
            )}

            {/* Eslatma */}
            {(n.first_payment_date || n.payment_day || n.notification_before) && (
              <section>
                <SectionTitle>{t("import.agreementAdd.preview.notificationSection")}</SectionTitle>
                <div className="grid grid-cols-2 gap-x-6 gap-y-4 rounded-card border border-grey-100 px-4 py-4 sm:grid-cols-3">
                  <Pair
                    label={t("import.agreementAdd.fields.first_payment_date")}
                    value={plain(n.first_payment_date)}
                  />
                  <Pair label={t("import.agreementAdd.fields.payment_day")} value={plain(n.payment_day)} />
                  {n.notification_before.trim() && (
                    <Pair
                      label={t("import.agreementAdd.fields.notification_before")}
                      value={n.notification_before.trim()}
                    />
                  )}
                </div>
              </section>
            )}
          </div>
        )}

        {/* Footer — Tahrirlash (cancel/rollback) · Yuklab olish (only with PDF) · Tasdiqlash.
            On phones the download button drops its label so all three fit one row. */}
        <div className="flex justify-end gap-2 border-t border-grey-100 px-4 py-4 sm:gap-3 sm:px-6">
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            className="flex items-center gap-2 whitespace-nowrap rounded-control border border-grey-200 px-4 py-2.5 text-[14px] font-medium text-grey-700 hover:bg-grey-50 disabled:opacity-60 sm:px-5"
          >
            <Pencil size={15} strokeWidth={2} />
            {t("import.agreementAdd.preview.editBtn")}
          </button>
          {hasPdf && (
            <button
              type="button"
              onClick={onDownload}
              disabled={isPending}
              aria-label={t("import.agreementAdd.preview.downloadBtn")}
              className="flex items-center gap-2 whitespace-nowrap rounded-control border border-grey-200 px-4 py-2.5 text-[14px] font-medium text-grey-700 hover:bg-grey-50 disabled:opacity-60 sm:px-5"
            >
              <Download size={15} strokeWidth={2} />
              <span className="hidden sm:inline">{t("import.agreementAdd.preview.downloadBtn")}</span>
            </button>
          )}
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            className="flex items-center gap-2 whitespace-nowrap rounded-control bg-green px-5 py-2.5 text-[14px] font-semibold text-white transition hover:bg-green-dark active:scale-95 disabled:opacity-60 disabled:active:scale-100 sm:px-6"
          >
            {isPending ? (
              <Loader2 size={16} strokeWidth={2} className="animate-spin" />
            ) : (
              <CheckCircle2 size={16} strokeWidth={2} />
            )}
            {isPending ? t("app.loading") : t("import.agreementAdd.preview.confirmBtn")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
